/**
 * Sequencer — step sequencer that drives notes AND a per-step
 * modulation lane.
 *
 * Two output channels:
 *
 *   - **Note output**: when `enabled === 'on'` and the current step is
 *     `active`, the sequencer fires the configured downstream
 *     `onStep(note, velocity, when?)` and schedules a matching
 *     `onRelease(note)` after `step.length` step durations.
 *
 *   - **Mod output**: a `ConstantSourceNode` whose offset is updated
 *     to `step.modValue` (mapped from [0,1] UI to [-1,+1] DC) on
 *     every tick. Registered with the router as the `STEP` source so
 *     the mod matrix can route it to any AudioParam.
 *
 * Timing follows the same setTimeout-driven scheduler as the arp —
 * not sample-accurate, but step rates land far below audio rate so
 * drift is inaudible at any musical BPM.
 *
 * The sequencer is a SynthModule so it slots into the existing
 * preset capture/apply flow without special-casing. Its parameters
 * (enabled / rate / length / swing) round-trip through the standard
 * module path; per-step data is serialized alongside via a custom
 * `ModuleState.params.steps` JSON blob.
 */

import {
  EnumParameter,
  NumericParameter,
  type AnyParameter,
} from '../params/Parameter';
import type { AudioEngine } from './AudioEngine';
import type { ModuleState, SynthModule } from './modules/SynthModule';
import { mintId, readParamState, writeParamState } from './modules/util';
import type { Transport, SyncMode } from './Transport';

/** Step rates — full division set, no `'free'` (the seq is rhythmic). */
const SEQ_RATES: readonly SyncMode[] = [
  '1/2',
  '1/4',
  '1/4T',
  '1/8',
  '1/8T',
  '1/16',
  '1/16T',
];

/** Maximum number of steps. 32 is enough for two-bar 16th-note phrases
 *  and keeps the UI from sprawling. */
export const SEQ_MAX_STEPS = 32;

/** Default total step count (the visible / playable length is
 *  controlled by the `length` parameter; the underlying `steps`
 *  array always sits at SEQ_MAX_STEPS so changing length doesn't
 *  destroy data). */
export const SEQ_DEFAULT_STEPS = 16;

export interface SequencerStep {
  /** When false, the step is silent — clock advances past it without
   *  firing a note. The mod value still updates so a "rest" step can
   *  still drive automation. */
  active: boolean;
  /** MIDI note number, 0..127. Defaults sit in the C minor pentatonic
   *  middle range so a fresh sequencer sounds at least musical. */
  note: number;
  /** Velocity, 0..1. */
  velocity: number;
  /** Length in steps (1 = single step, 2 = held for two steps, etc).
   *  Capped at SEQ_MAX_STEPS so the ratchet timer can't run away. */
  length: number;
  /** Per-step modulation value, [0, 1]. Mapped to [-1, +1] for the
   *  ConstantSourceNode offset, so 0.5 = neutral. */
  modValue: number;
}

export type SequencerStepHandler = (
  note: number,
  velocity: number,
  when?: number,
) => void;
export type SequencerReleaseHandler = (note: number) => void;

export type SequencerListener = (sequencer: Sequencer) => void;

/**
 * Default seed pattern — a simple two-bar phrase in C minor pentatonic
 * with all steps active so a fresh sequencer plays *something* the
 * moment the user hits "on". Mod values trace a gentle wave so the
 * routed parameter visibly moves.
 */
function defaultSteps(): SequencerStep[] {
  // C minor pentatonic root notes.
  const PITCHES = [60, 63, 65, 67, 70, 72, 70, 67, 65, 63, 60, 58, 60, 63, 65, 67];
  const out: SequencerStep[] = [];
  for (let i = 0; i < SEQ_MAX_STEPS; i++) {
    out.push({
      active: i < SEQ_DEFAULT_STEPS, // first 16 active by default
      note: PITCHES[i % PITCHES.length] ?? 60,
      velocity: 0.8,
      length: 1,
      // Slow triangle 0..1..0..1 across the bar so the routed
      // destination has a visible default movement.
      modValue: 0.5 + 0.5 * Math.sin((i / SEQ_DEFAULT_STEPS) * Math.PI * 2),
    });
  }
  return out;
}

export class Sequencer implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'sequencer' as const;
  readonly displayName = 'SEQ';
  readonly id: string;
  bypass = false;

  /** Sequencer is a source — no input. Output is the mod-lane signal. */
  readonly input: AudioNode | null = null;
  /** Per-step modulation signal, exposed as the `STEP` mod source. */
  modOutput: ConstantSourceNode | null = null;

  readonly enabled = new EnumParameter<'off' | 'on'>({
    name: 'Enabled',
    default: 'off',
    options: ['off', 'on'],
    randomizable: false,
  });

  readonly rate = new EnumParameter<SyncMode>({
    name: 'Rate',
    default: '1/16',
    options: SEQ_RATES,
    randomizable: false,
  });

  /** Number of steps that play before looping. Editable steps beyond
   *  this length stay in `steps` but are skipped during playback. */
  readonly length = new NumericParameter({
    name: 'Length',
    default: SEQ_DEFAULT_STEPS,
    min: 1,
    max: SEQ_MAX_STEPS,
    curve: 'linear',
    smoothingMs: 0,
    formatter: (v) => `${Math.round(v)}`,
    randomizable: false,
  });

  /** Swing as a fraction of the step interval. Even-indexed steps fire
   *  on the beat; odd-indexed steps are delayed by `swing × stepMs`.
   *  0.5 = perfect triplet feel, 0 = straight. */
  readonly swing = new NumericParameter({
    name: 'Swing',
    default: 0,
    min: 0,
    max: 0.5,
    curve: 'linear',
    smoothingMs: 0,
    formatter: (v) => `${Math.round(v * 100)}%`,
    randomizable: false,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.enabled,
    this.rate,
    this.length,
    this.swing,
  ];

  /** All steps in the pattern. Always SEQ_MAX_STEPS long; the
   *  effective playback range is [0, length.value). Editing a step
   *  past length is fine and persists, just doesn't audibly play
   *  until length is bumped up. */
  readonly steps: SequencerStep[] = defaultSteps();

  get output(): AudioNode | null {
    return this.modOutput;
  }

  private readonly transport: Transport;
  private engine: AudioEngine | null = null;
  private stepHandler: SequencerStepHandler | null = null;
  private releaseHandler: SequencerReleaseHandler | null = null;

  /** Index of the step that will fire on the *next* tick. Updated
   *  after each scheduled tick so the indicator UI reflects what's
   *  currently audible. */
  private cursor = 0;
  /** Index of the step the UI should highlight as "now playing".
   *  Differs from cursor between scheduling and the tick callback. */
  private currentStepIndex = -1;
  /** Steps remaining on an in-flight tie. When a step fires with
   *  length > 1, the next (length - 1) ticks become carriers — they
   *  still update the mod lane but skip note firing so the held
   *  note rings unimpeded. Same convention `generateMusicalSequence`
   *  uses for random ties. */
  private tieRemaining = 0;
  /**
   * Audio-context time at which the next tick should fire. Used as
   * an absolute anchor so each scheduled tick self-corrects against
   * the audio clock — setTimeout drift doesn't accumulate across a
   * pattern, which is what kept the sequencer falling behind a
   * sample-accurate scheduler like the metronome.
   */
  private nextTickTime = 0;
  private stepTimer: ReturnType<typeof setTimeout> | null = null;
  /** Per-active note: a release timer so length>1 notes ring out
   *  correctly. Keyed by MIDI note so two simultaneous steps on the
   *  same pitch don't trample each other's release schedules. */
  private readonly noteReleaseTimers = new Map<
    number,
    ReturnType<typeof setTimeout>
  >();

  private readonly listeners = new Set<SequencerListener>();

  constructor(transport: Transport, id?: string) {
    this.id = id ?? mintId('seq');
    this.transport = transport;

    // Toggling enabled mid-play should cleanly stop any pending step
    // / release timers so the disable doesn't leave a hanging note or
    // ringing mod value.
    this.enabled.subscribe((v) => {
      if (v === 'on') this.startClock();
      else this.stopClock();
    });
  }

  init(engine: AudioEngine): void {
    if (this.modOutput) return;
    this.engine = engine;
    const ctx = engine.context;
    this.modOutput = ctx.createConstantSource();
    // Initial offset reflects step 0's mod value. Using setValueAtTime
    // (not the constructor's offset.value) so we go through the same
    // path the runtime tick uses for consistency.
    const initialDc = (this.steps[0]?.modValue ?? 0.5) * 2 - 1;
    this.modOutput.offset.setValueAtTime(initialDc, ctx.currentTime);
    this.modOutput.start();
  }

  dispose(): void {
    this.panic();
    this.listeners.clear();
    if (this.modOutput) {
      try {
        this.modOutput.stop();
      } catch {
        /* already stopped */
      }
      this.modOutput.disconnect();
      this.modOutput = null;
    }
    this.engine = null;
  }

  setHandlers(opts: {
    onStep: SequencerStepHandler;
    onRelease: SequencerReleaseHandler;
  }): void {
    this.stepHandler = opts.onStep;
    this.releaseHandler = opts.onRelease;
  }

  /**
   * Index of the step currently being played (or -1 when stopped).
   * The UI uses this to highlight the cursor.
   */
  getCurrentStepIndex(): number {
    return this.currentStepIndex;
  }

  /**
   * Patch a single step. Partial updates supported — fields not
   * supplied keep their current values. Fires the listeners so the
   * UI can re-render the affected cell.
   */
  setStep(index: number, patch: Partial<SequencerStep>): void {
    const s = this.steps[index];
    if (!s) return;
    if (patch.active !== undefined) s.active = patch.active;
    if (patch.note !== undefined) {
      s.note = Math.max(0, Math.min(127, Math.round(patch.note)));
    }
    if (patch.velocity !== undefined) {
      s.velocity = Math.max(0, Math.min(1, patch.velocity));
    }
    if (patch.length !== undefined) {
      s.length = Math.max(1, Math.min(SEQ_MAX_STEPS, Math.round(patch.length)));
    }
    if (patch.modValue !== undefined) {
      s.modValue = Math.max(0, Math.min(1, patch.modValue));
    }
    this.emit();
  }

  /**
   * Bulk-replace step data. Used by the musical randomizer + pattern
   * load flows to swap in a fresh pattern in one shot instead of
   * iterating `setStep` (which would fire the listener N times).
   * Steps shorter than the array are filled with defaults; longer
   * arrays are truncated at SEQ_MAX_STEPS.
   */
  setSteps(steps: readonly SequencerStep[]): void {
    for (let i = 0; i < this.steps.length; i++) {
      const incoming = steps[i];
      if (!incoming) continue;
      const target = this.steps[i]!;
      target.active = !!incoming.active;
      target.note = Math.max(0, Math.min(127, Math.round(incoming.note)));
      target.velocity = Math.max(0, Math.min(1, incoming.velocity));
      target.length = Math.max(
        1,
        Math.min(SEQ_MAX_STEPS, Math.round(incoming.length)),
      );
      target.modValue = Math.max(0, Math.min(1, incoming.modValue));
    }
    this.emit();
  }

  /** Stop the clock + release any active note. Called on enabled→off,
   *  preset load (via resetToDefaults / panic), and dispose. */
  panic(): void {
    this.stopClock();
    for (const [note, timer] of this.noteReleaseTimers) {
      clearTimeout(timer);
      this.releaseHandler?.(note);
    }
    this.noteReleaseTimers.clear();
    this.cursor = 0;
    this.currentStepIndex = -1;
    this.tieRemaining = 0;
    this.emit();
  }

  subscribe(fn: SequencerListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  // ── Module serialization ──────────────────────────────────────────────

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: {
        ...writeParamState(this.parameters),
        // Steps go in as a JSON-encoded blob so the existing
        // ModuleState shape (Record<string, number | string>) stays
        // unchanged. JSON keeps the field self-contained and
        // versionable if we ever need to migrate the per-step shape.
        steps: JSON.stringify(this.steps),
      },
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
    const raw = state.params['steps'];
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as Partial<SequencerStep>[];
        for (let i = 0; i < this.steps.length; i++) {
          const incoming = parsed[i];
          if (!incoming) continue;
          // Validate each field — corrupted JSON or future-shape
          // entries shouldn't bring down the load.
          const target = this.steps[i]!;
          if (typeof incoming.active === 'boolean') target.active = incoming.active;
          if (typeof incoming.note === 'number') {
            target.note = Math.max(0, Math.min(127, Math.round(incoming.note)));
          }
          if (typeof incoming.velocity === 'number') {
            target.velocity = Math.max(0, Math.min(1, incoming.velocity));
          }
          if (typeof incoming.length === 'number') {
            target.length = Math.max(
              1,
              Math.min(SEQ_MAX_STEPS, Math.round(incoming.length)),
            );
          }
          if (typeof incoming.modValue === 'number') {
            target.modValue = Math.max(0, Math.min(1, incoming.modValue));
          }
        }
      } catch {
        /* malformed — keep defaults */
      }
    }
    this.emit();
  }

  /**
   * Reset all parameters AND step data to their defaults. Called by
   * PresetManager when a loaded preset doesn't include sequencer
   * state — same hook as the arp's resetToDefaults.
   */
  resetToDefaults(): void {
    this.panic();
    for (const p of this.parameters) p.reset('preset');
    const fresh = defaultSteps();
    for (let i = 0; i < this.steps.length; i++) {
      this.steps[i] = fresh[i]!;
    }
    this.emit();
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private startClock(): void {
    if (this.stepTimer) return;
    this.cursor = 0;
    this.tieRemaining = 0;
    // Anchor the next tick to the current audio time so the very
    // first step starts on a clean reference point. A tiny lead-in
    // (5 ms) absorbs the inevitable scheduler latency without being
    // audibly late.
    const now = this.audioNow();
    this.nextTickTime = now + 0.005;
    this.scheduleNextTick();
  }

  private stopClock(): void {
    if (this.stepTimer) {
      clearTimeout(this.stepTimer);
      this.stepTimer = null;
    }
    this.currentStepIndex = -1;
    this.emit();
  }

  /**
   * Schedule the next tick relative to the audio clock. We compute
   * how far in the future `nextTickTime` is, then fire after that
   * many milliseconds. After the tick lands we advance
   * `nextTickTime` by the step duration — so any setTimeout jitter
   * is absorbed on the next cycle instead of compounding. Same
   * drift-correction trick the metronome uses internally.
   */
  private scheduleNextTick(): void {
    const now = this.audioNow();
    const delayMs = Math.max(0, (this.nextTickTime - now) * 1000);
    this.stepTimer = setTimeout(() => {
      this.tick();
      if (this.enabled.value !== 'on') return;
      // Advance the anchor by the step's intended duration. Reading
      // stepDurationSec each time picks up live BPM / rate / swing
      // changes within one step.
      this.nextTickTime += this.stepDurationSec(this.cursor);
      // If JS got blocked long enough that we slipped past the next
      // anchor, jump forward to the nearest future tick boundary
      // instead of trying to catch up with rapid-fire ticks (which
      // would sound worse than a hiccup).
      const after = this.audioNow();
      if (this.nextTickTime < after) this.nextTickTime = after + 0.001;
      this.scheduleNextTick();
    }, delayMs);
  }

  /** AudioContext time in seconds. Falls back to `performance.now()`
   *  when no engine is attached (tests / pre-unlock). */
  private audioNow(): number {
    if (this.engine) return this.engine.context.currentTime;
    return (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
  }

  /** Step duration in seconds, factoring in swing on odd-index steps.
   *  `index` is the step about to fire — its own swing setting
   *  determines whether this step itself is delayed. */
  private stepDurationSec(index: number): number {
    const baseSec = this.stepSec();
    const swing = Math.max(0, Math.min(0.5, this.swing.value));
    if (index % 2 === 1 && swing > 0) return baseSec * (1 + swing);
    return baseSec;
  }

  private stepSec(): number {
    const rate = this.rate.value;
    if (rate === 'free') return 0.125; // shouldn't happen — guard
    return this.transport.divisionToSeconds(rate);
  }

  /** Step duration in ms, used by the note-release timer in `tick`. */
  private stepMs(): number {
    return Math.max(1, this.stepSec() * 1000);
  }

  /**
   * Fire whatever step is currently under `cursor`, then advance.
   * Active steps fire onStep + schedule onRelease at length×stepMs.
   * Inactive steps still update modOutput so a "rest" can still
   * drive automation.
   */
  private tick(): void {
    const len = Math.max(1, Math.round(this.length.value));
    const stepIndex = this.cursor % len;
    const step = this.steps[stepIndex];
    this.currentStepIndex = stepIndex;
    if (!step) return;

    // Mod output — always updated, active or not. Mapped from the
    // [0,1] UI range to [-1,+1] DC so a step value of 0.5 = neutral
    // and the LFO-style ±1 swing matches the rest of the modulation
    // sources. setTargetAtTime smooths the per-step jump so the
    // routed parameter doesn't click on every step.
    if (this.modOutput && this.engine) {
      const dc = step.modValue * 2 - 1;
      const t = this.engine.context.currentTime;
      this.modOutput.offset.cancelScheduledValues(t);
      // Time constant short relative to step length so the new value
      // settles within the step but doesn't snap (avoids zipper).
      this.modOutput.offset.setTargetAtTime(dc, t, 0.005);
    }

    // Tie carrier — the previous active step is still ringing
    // through this slot. Skip note firing so the held note isn't
    // chopped by a re-trigger; the mod value above already updated
    // so automation still flows.
    if (this.tieRemaining > 0) {
      this.tieRemaining--;
      this.cursor = (this.cursor + 1) % len;
      this.emit();
      return;
    }

    if (step.active && this.stepHandler) {
      // Release any in-flight note on the same pitch so a re-trigger
      // doesn't leave a hanging release timer for the previous one.
      const existing = this.noteReleaseTimers.get(step.note);
      if (existing) {
        clearTimeout(existing);
        this.releaseHandler?.(step.note);
        this.noteReleaseTimers.delete(step.note);
      }
      this.stepHandler(step.note, step.velocity);
      const holdMs = this.stepMs() * step.length;
      const note = step.note;
      const timer = setTimeout(() => {
        this.noteReleaseTimers.delete(note);
        this.releaseHandler?.(note);
      }, holdMs);
      this.noteReleaseTimers.set(note, timer);
      // Steps the tie covers are silent for the rest of the hold.
      // length=1 = no carriers, plays as a normal step.
      if (step.length > 1) this.tieRemaining = step.length - 1;
    }

    this.cursor = (this.cursor + 1) % len;
    this.emit();
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }
}
